import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { TimesheetSchema, TimesheetCreateInputSchema, TimesheetUpdateInputSchema, type Timesheet, type TimesheetCreateInput, type TimesheetUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `timesheet` (temps de travail, org-wide).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`TimesheetCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { TimesheetCreateInput };

/** Nom de la collection Firestore de la famille timesheet. */
export const TIMESHEETS_COLLECTION = 'timesheets';

/**
 * Repository typé pour la famille `timesheet`.
 * Toute lecture est validée par `TimesheetSchema`, toute écriture par
 * `TimesheetCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface TimesheetRepository {
  create(input: TimesheetCreateInput): Promise<Timesheet>;
  update(id: string, patch: TimesheetUpdateInput): Promise<Timesheet>;
  list(options?: ListOptions): Promise<Timesheet[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Timesheet | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Timesheet>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Timesheet>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Timesheet>;
}

const timesheetConverter = createConverter<Timesheet>((raw: DocumentData) => TimesheetSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createTimesheetRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): TimesheetRepository {
  const col = collection(db, TIMESHEETS_COLLECTION).withConverter(timesheetConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Timesheet>(db, col, TIMESHEETS_COLLECTION, getActorId);

  return {
    async create(input: TimesheetCreateInput): Promise<Timesheet> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = TimesheetCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.projectId !== undefined) optional['projectId'] = fields.projectId;
      if (fields.taskId !== undefined) optional['taskId'] = fields.taskId;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.durationMinutes !== undefined) optional['durationMinutes'] = fields.durationMinutes;
      if (fields.billable !== undefined) optional['billable'] = fields.billable;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.shiftId !== undefined) optional['shiftId'] = fields.shiftId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        workerId: fields.workerId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Timesheet);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return TimesheetSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: TimesheetUpdateInput): Promise<Timesheet> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = TimesheetUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, TIMESHEETS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`timesheet ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Timesheet[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Timesheet | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
